import { Box, Button, Flex, IconButton, ProgressIndicator, Text } from '@rocket/ui';
import type { ChangeEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { LuFile, LuPaperclip, LuX } from 'react-icons/lu';
import { useResumableJobBanner } from '../hooks/useResumableJobBanner';
import { useFtueHost } from '../host';
import { type ResumableExtraction, useOnboarding } from '../OnboardingContext';
import type { ExtractedSection, ExtractionProgress, SlotSurfaceForms } from '../onboarding-api';
import {
  extractionCurrentTotal,
  extractionPercentComplete,
  extractionProgressMessage,
  extractRulesFromContractsStream,
} from '../onboarding-api';
import { FirstPartyPaperChatBox } from './FirstPartyPaperChatBox';
import { ReviewChatBox } from './ReviewChatBox';

const MAX_FILES = 5;

interface Props {
  paperType: 'first_party' | 'third_party';
  playbookId?: string;
  contractType: string;
  initialSections?: Array<ExtractedSection>;
  onBack: () => void;
  onRulesExtracted: (sections: Array<ExtractedSection>, slots?: SlotSurfaceForms) => void;
  onExtractionStateChange?: (isExtracting: boolean) => void;
  /** When false, hides the rule creation chat boxes */
  enableChatRuleCreation?: boolean;
  /** An extraction job found still running (or just finished) from a
   * previous session -- offer to resume watching it instead of forcing a
   * resubmit, which for large documents can mean redoing 60+ minutes of work. */
  resumableJob?: ResumableExtraction | null;
  /** True for exactly one mount after the user already chose Resume on the
   * global ResumableExtractionBanner -- skip this component's own
   * Resume/Discard prompt and start polling immediately instead of asking
   * again. */
  autoResume?: boolean;
  /** Called once the auto-resume above has been started (or, if there was
   * no resumable job, decided against), so a later remount of this same
   * instance doesn't re-trigger it. */
  onAutoResumeHandled?: () => void;
}

export function OnboardingUpload({
  paperType,
  playbookId,
  contractType,
  onBack,
  onRulesExtracted,
  onExtractionStateChange,
  enableChatRuleCreation = true,
  resumableJob,
  autoResume = false,
  onAutoResumeHandled,
}: Props) {
  const { authedFetch, bbAxios, handleUnauthorized, isAuthError } = useFtueHost();
  const { clearResumableExtraction, discardResumableExtraction } = useOnboarding();
  const [files, setFiles] = useState<Array<File>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<ExtractionProgress | null>(null);
  const [_totalRulesFound, setTotalRulesFound] = useState(0);

  const {
    showResumeBanner,
    resumingJobId,
    resumePercent,
    resumeMessage,
    isDiscarding,
    discardError,
    handleResume,
    handleDiscard,
  } = useResumableJobBanner(bbAxios, {
    resumableJob,
    autoResume,
    onAutoResumeHandled,
    onExtractionStateChange,
    onRulesExtracted,
    onResumeError: (message) => setError(`Resumed extraction failed: ${message}`),
    clearResumableExtraction,
    discardResumableExtraction,
  });

  const _isThirdParty = paperType === 'third_party';
  const fileInputRef = useRef<HTMLInputElement>(null);
  // A fresh extraction and a resumed one are mutually exclusive but both
  // should disable file pickers/nav the same way.
  const isBusy = isLoading || !!resumingJobId;

  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    setFiles((prev) => {
      const existing = new Set(prev.map((f) => f.name));
      const newFiles = picked.filter((f) => !existing.has(f.name));
      const combined = [...prev, ...newFiles];
      // Limit to MAX_FILES total
      const limited = combined.slice(0, MAX_FILES);
      if (combined.length > MAX_FILES) {
        setError(`Maximum ${MAX_FILES} files allowed. Only the first ${MAX_FILES} were added.`);
      }
      return limited;
    });
    // reset so same file can be re-added after removal
    e.target.value = '';
  };

  // Debug: Log progress state changes
  useEffect(() => {
    if (progress) {
      console.debug('[OnboardingUpload] Progress state updated:', {
        rules_extracted: progress.rules_extracted,
        rules_from_file: progress.rules_from_file,
        file: progress.file,
        files_processed: progress.files_processed,
      });
    }
  }, [progress]);

  const handleExtract = async () => {
    if (files.length === 0) {
      return;
    }
    setIsLoading(true);
    onExtractionStateChange?.(true);
    setError(null);
    setProgress(null);
    setTotalRulesFound(0);
    try {
      const { sections, slotSurfaceForms } = await extractRulesFromContractsStream(
        authedFetch,
        { files, paperType, contractType, playbookId },
        (p) => {
          console.debug('[OnboardingUpload] onProgress callback fired:', {
            rules_extracted: p.rules_extracted,
            rules_from_file: p.rules_from_file,
            file: p.file,
            files_processed: p.files_processed,
          });
          setTotalRulesFound(p.rules_extracted);
          console.debug('[OnboardingUpload] About to call setProgress with:', p);
          setProgress(p);
        }
      );

      // Show extracted rules immediately so the user can start reviewing
      onRulesExtracted(sections, slotSurfaceForms);
    } catch (err) {
      if (isAuthError(err)) {
        handleUnauthorized();
        return;
      }
      const message =
        err instanceof Error ? err.message : 'Failed to extract rules. Please try again.';
      setError(message);
    } finally {
      setIsLoading(false);
      onExtractionStateChange?.(false);
      setProgress(null);
    }
  };

  const accept = '.docx,.pdf,.doc,.txt';
  const hintText =
    'Upload edited contracts (.docx), playbook documents (.docx, .pdf, .txt), or both.';

  return (
    <Flex.V h="100%" gap="0">
      {/* Compact upload strip */}
      <Flex.V gap="2" px="4" pt="4" pb="3" borderBottom="1px solid" borderColor="border">
        {/* Row 1: title */}
        <Flex.H align="center" justify="space-between">
          <Text fontWeight="600" fontSize="sm">
            Upload Source Material
          </Text>
        </Flex.H>

        {/* Resume banner: an extraction from a previous session is still
            running (or just finished) -- offer to resume watching it rather
            than resubmitting a document that may take 60+ minutes to redo. */}
        {showResumeBanner && resumableJob && (
          <Box bg="blue.subtle" borderColor="blue.muted" borderWidth="1px" borderRadius="md" p="2">
            <Text fontSize="xs" fontWeight="600" color="blue.fg">
              Extraction still running for{' '}
              {resumableJob.filenames?.join(', ') || 'a previous upload'}
            </Text>
            <Text fontSize="xs" color="blue.fg" mb="1">
              This can take a while for large documents. You can pick back up where it left off, or
              discard it and start over.
            </Text>
            <Flex.H gap="2">
              <Button size="xs" colorPalette="blue" onClick={handleResume}>
                Resume
              </Button>
              <Button
                size="xs"
                variant="outline"
                onClick={handleDiscard}
                disabled={isDiscarding}
                loading={isDiscarding}
              >
                Discard
              </Button>
            </Flex.H>
            {discardError && (
              <Text fontSize="xs" color="red.fg" mt="1">
                {discardError}
              </Text>
            )}
          </Box>
        )}

        {/* Resuming: poll progress from the job doc instead of a live stream */}
        {resumingJobId && (
          <Box bg="blue.subtle" borderColor="blue.muted" borderWidth="1px" borderRadius="md" p="2">
            <Text fontSize="xs" fontWeight="600" color="blue.fg">
              Resuming extraction… {resumePercent > 0 ? `${resumePercent}%` : ''}
            </Text>
            {resumeMessage && (
              <Text fontSize="xs" color="blue.fg" truncate>
                {resumeMessage}
              </Text>
            )}
          </Box>
        )}

        {/* Row 2: inline browse button + file chips */}
        <Flex.H gap="2" align="center" flexWrap="wrap">
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            multiple
            style={{ display: 'none' }}
            onChange={handleFileInputChange}
          />
          <Button
            size="xs"
            variant="outline"
            flexShrink="0"
            cursor="pointer"
            disabled={isBusy || files.length >= MAX_FILES}
            onClick={() => fileInputRef.current?.click()}
          >
            <LuPaperclip size={12} />
            Browse files
          </Button>
          <Text fontSize="xs" color="fg.subtle" flexShrink="0">
            {files.length >= MAX_FILES ? `Maximum ${MAX_FILES} files reached` : hintText}
          </Text>
          {files.map((file) => (
            <Flex.H
              key={file.name}
              align="center"
              gap="1"
              px="2"
              py="1"
              bg="bg.subtle"
              borderWidth="1px"
              borderColor="border"
              borderRadius="md"
              maxW="40"
            >
              <LuFile size={11} />
              <Text fontSize="xs" truncate flex="1" title={file.name}>
                {file.name}
              </Text>
              <IconButton
                size="xs"
                variant="ghost"
                aria-label="Remove file"
                color="fg.subtle"
                onClick={() => setFiles((prev) => prev.filter((f) => f.name !== file.name))}
                disabled={isLoading}
              >
                <LuX size={11} />
              </IconButton>
            </Flex.H>
          ))}
        </Flex.H>

        {/* Status (only when active or errored) */}
        {(isLoading || error) && (
          <Box>
            {error ? (
              <Box
                bg="red.subtle"
                borderColor="red.muted"
                borderWidth="1px"
                borderRadius="md"
                p="2"
              >
                <Text fontSize="xs" color="red.fg">
                  {error}
                </Text>
              </Box>
            ) : isLoading ? (
              <ProgressIndicator
                message={extractionProgressMessage(progress)}
                current={extractionCurrentTotal(progress).current}
                total={extractionCurrentTotal(progress).total}
                percentComplete={extractionPercentComplete(progress)}
              />
            ) : null}
          </Box>
        )}

        {/* Actions */}
        <Flex.H gap="2" justify="flex-end">
          <Button variant="ghost" size="sm" onClick={onBack} disabled={isBusy}>
            Back
          </Button>
          {files.length > 0 ? (
            <Button
              colorPalette="gray"
              size="sm"
              disabled={isBusy}
              loading={isLoading}
              loadingText="Extracting…"
              onClick={handleExtract}
            >
              Extract Rules
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              disabled={isBusy}
              onClick={() => onRulesExtracted([])}
            >
              Next
            </Button>
          )}
        </Flex.H>
      </Flex.V>

      {/* Chat — fills all remaining space */}
      {enableChatRuleCreation && (
        <Flex.V flex="1" minH="0" px="4" py="3" gap="2">
          <Text fontSize="xs" color="fg.muted" fontWeight="500">
            Or add rules directly with the assistant
          </Text>
          {paperType === 'first_party' ? (
            <FirstPartyPaperChatBox
              playbookId={playbookId}
              contractType={contractType}
              fillHeight
            />
          ) : (
            <ReviewChatBox playbookId={playbookId} contractType={contractType} fillHeight />
          )}
        </Flex.V>
      )}
    </Flex.V>
  );
}
